<template>
  <div class="checklist-wrapper">
    <div v-if="errorMessages.length" class="checklist-alerts">
      <div v-for="(error, index) in errorMessages" :key="'error-' + index" class="alert-item alert-danger">
        <span>{{ error }}</span>
        <button type="button" class="alert-close" @click.stop.prevent="removeError(index)">&times;</button>
      </div>
    </div>
    <div v-if="successMessage" class="checklist-alerts">
      <div class="alert-item alert-success">
        <span>{{ successMessage }}</span>
        <button type="button" class="alert-close" @click.stop.prevent="successMessage = null">&times;</button>
      </div>
    </div>

    <div class="checklist-body">
      <div v-if="checkList.length && checkList[0].description" class="checklist-description">
        <p>{{ checkList[0].description }}</p>
      </div>
      <div class="checklist-items">
        <label v-for="(item, index) in checkList"
               :key="'check-' + index"
               :class="{ 'checklist-item-checked': userCheckListAnswer[index] && userCheckListAnswer[index].value }"
               class="checklist-item">
          <input :id="`checkbox-${index}`"
                 type="checkbox"
                 class="checklist-checkbox"
                 :checked="userCheckListAnswer[index] && userCheckListAnswer[index].value"
                 @change="userChangeCheckItem(item.id, $event, index)" />
          <span class="checklist-label">{{ item.name }}</span>
          <span v-if="item.is_mandatory" class="checklist-mandatory-dot"></span>
        </label>
      </div>
      <p class="checklist-hint">
        <i class="uil-info-circle"></i>
        {{ $t('general.mandatory_items_hint') || 'Zorunlu olarak işaretlenen maddeleri tamamlamanız gerekmektedir.' }}
      </p>
    </div>

    <div class="checklist-footer">
      <button v-if="modal_name" class="btn-secondary-action" @click="closeModal">
        {{ $t("general.close") }}
      </button>
      <button v-if="!hideSubmit" class="btn-primary-action" @click="onSubmit">
        {{ $t("general.submit") }}
      </button>
    </div>
  </div>
</template>

<script>
import _ from "lodash";

export default {
  name: "CheckListDetail",
  props: {
    checkList: {
      required: true,
      default: () => [],
    },
    modal_name: {
      required: true,
    },
    modal_title: {
      required: false,
    }
  },
  data() {
    return {
      errorMessages: [],
      successMessage: null,
      userCheckListAnswer: [],
      hideSubmit: false
    };
  },
  methods: {
    userChangeCheckItem(checkListItemId, event, index) {
      this.userCheckListAnswer[index].value = event.target.checked;
      this.hideSubmit = false;
      this.checkList.every(data => {
        if (data.is_mandatory) {
          this.userCheckListAnswer.every(answer => {
            if (data.id === answer.id) {
              if (!answer.value) {
                this.hideSubmit = true;
              }
              return false;
            }
            return true;
          });
        }
        return !this.hideSubmit;
      });
    },
    onSubmit() {
      const self = this;
      this.$emit("onSubmit", {
        content_check_list_answers: this.userCheckListAnswer,
      }, (data) => {
        if (!data.status) {
          if (data.code !== 422) {
            self.errorMessages.push(self.$t(data.error.message));
            return;
          }
          self.showErrors(data.error.errors);
        } else {
          setTimeout(() => self.closeModal(), 1000);
        }
      });
    },
    showErrors(errors) {
      this.resetMessages();
      for (const error of Object.values(errors)) {
        if (Array.isArray(error)) {
          error.forEach(text => this.errorMessages.push(text));
        } else {
          this.errorMessages.push(error);
        }
      }
    },
    closeModal() {
      this.$emit('onClose', true);
      if (this.modal_name) this.$modal.hide(this.modal_name);
    },
    resetMessages() {
      this.errorMessages = [];
    },
    removeError(index) {
      this.errorMessages.splice(index, 1);
    },
  },
  watch: {
    errorMessages() {
      this.debouncedResetValues();
    },
  },
  mounted() {
    this.userCheckListAnswer = this.checkList.map(item => ({
      id: item.id,
      value: false
    }));
    this.hideSubmit = this.checkList.some(item => item.is_mandatory);
  },
  created() {
    this.debouncedResetValues = _.debounce(this.resetMessages, 2500);
  },
};
</script>

<style scoped>
.checklist-wrapper {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 48px);
  max-height: calc(100vh - 48px);
  overflow: hidden;
  box-sizing: border-box;
  padding: 16px;
}

/* Alerts */
.checklist-alerts {
  margin-bottom: 12px;
  max-width: 640px;
  width: 100%;
  margin-left: auto;
  margin-right: auto;
}

.alert-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  border-radius: 10px;
  font-size: 14px;
  line-height: 1.4;
  margin-bottom: 8px;
}

.alert-item:last-child {
  margin-bottom: 0;
}

.alert-danger {
  background: #fef2f2;
  color: #dc2626;
  border: 1px solid #fecaca;
}

.alert-success {
  background: #f0fdf4;
  color: #16a34a;
  border: 1px solid #bbf7d0;
}

.alert-close {
  border: none;
  background: transparent;
  font-size: 18px;
  line-height: 1;
  opacity: 0.5;
  padding: 0 0 0 12px;
  min-width: 24px;
  min-height: 24px;
}

/* Description */
.checklist-description {
  margin-bottom: 16px;
  padding: 12px 16px;
  background: #f9fafb;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
}

.checklist-description p {
  margin: 0;
  font-size: 15px;
  line-height: 1.5;
  color: #374151;
}

/* Body */
.checklist-body {
  flex: 1 1 0;
  overflow-y: auto;
  min-height: 0;
  -webkit-overflow-scrolling: touch;
  max-width: 640px;
  width: 100%;
  margin: 0 auto;
}

/* Checklist Items */
.checklist-items {
  display: flex;
  flex-direction: column;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  padding: 4px 16px;
}

.checklist-item {
  display: flex;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  min-height: 44px;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
}

.checklist-item:last-child {
  border-bottom: none;
}

.checklist-item:active {
  opacity: 0.7;
}

.checklist-item-checked {
  background: rgba(62, 65, 109, 0.06);
  border-radius: 10px;
  margin: 2px -8px;
  padding: 12px 8px;
}


.checklist-item-checked .checklist-checkbox {
  border-color: var(--primaryColor, #3e416d);
}

.checklist-checkbox {
  width: 20px;
  height: 20px;
  min-width: 20px;
  border-radius: 6px;
  border: 2px solid #d1d5db;
  cursor: pointer;
  accent-color: var(--primaryColor, #3e416d);
  margin: 0 !important;
}

.checklist-label {
  flex: 1;
  margin-left: 12px;
  font-size: 15px;
  line-height: 1.4;
  color: #333;
  user-select: none;
}

/* Zorunlu işaret — küçük turuncu nokta */
.checklist-mandatory-dot {
  width: 8px;
  height: 8px;
  min-width: 8px;
  border-radius: 50%;
  background: #f59e0b;
  margin-left: 8px;
  flex-shrink: 0;
}

/* Hint */
.checklist-hint {
  font-size: 13px;
  color: #6b7280;
  margin: 12px 0 0 0;
  line-height: 1.4;
}

.checklist-hint i {
  margin-right: 4px;
  font-size: 14px;
}

/* Footer */
.checklist-footer {
  flex-shrink: 0;
  display: flex;
  justify-content: flex-end;
  padding: 12px 0 0;
  gap: 8px;
  border-top: 1px solid #e5e7eb;
  margin-top: 16px;
  max-width: 640px;
  width: 100%;
  margin-left: auto;
  margin-right: auto;
}

.btn-primary-action,
.btn-secondary-action {
  min-height: 48px;
  padding: 12px 24px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  border: none;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
}

.btn-primary-action {
  background: var(--primaryColor, #3e416d);
  color: #fff;
}

.btn-primary-action i {
  margin-right: 4px;
}

.btn-secondary-action {
  background: #f3f4f6;
  color: #374151;
  border: 1px solid #d1d5db;
}

.btn-primary-action:active,
.btn-secondary-action:active {
  transform: scale(0.98);
  opacity: 0.85;
}

/* Mobile */
@media (max-width: 767px) {
  .checklist-wrapper {
    padding: 12px;
  }

  .checklist-item {
    padding: 12px;
  }

  .btn-primary-action:hover,
  .btn-secondary-action:hover {
    transform: none;
  }

  .checklist-item:hover {
    border-color: #e5e7eb;
  }

  .checklist-item-checked:hover {
    border-color: var(--primaryColor, #3e416d);
  }
}
</style>
